const LABEL_TO_FIELD = {
  storename: "name",
  streetaddress: "address",
  city: "city",
  state: "state",
  postalcode: "zip",
  zipcode: "zip",
  latitudelongitude: "coordinates",
};

const toSlug = (value) =>
  value
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeLabel = (label) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const parseCoordinates = (coordinates) => {
  if (!coordinates) {
    return null;
  }

  const [lat, lng] = coordinates.split(",").map((value) => parseFloat(value));

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

const getRetailerText = (retailer) => {
  if (!retailer || !Array.isArray(retailer.retailer_info)) {
    return "";
  }

  const retailerInfo = retailer.retailer_info[0];
  return retailerInfo && typeof retailerInfo.text === "string"
    ? retailerInfo.text
    : "";
};

export const parseHempRetailerText = (text) => {
  if (!text) {
    return null;
  }

  const fields = text.split("\n").reduce((result, line) => {
    const separatorIndex = line.indexOf(":");

    if (separatorIndex === -1) {
      return result;
    }

    const label = normalizeLabel(line.slice(0, separatorIndex).trim());
    const field = LABEL_TO_FIELD[label];

    if (!field) {
      return result;
    }

    return {
      ...result,
      [field]: line.slice(separatorIndex + 1).trim(),
    };
  }, {});

  const coordinates = parseCoordinates(fields.coordinates);

  if (
    !fields.name ||
    !fields.address ||
    !fields.city ||
    !fields.state ||
    !fields.zip ||
    !coordinates
  ) {
    return null;
  }

  return {
    name: fields.name,
    address: fields.address,
    city: fields.city,
    state: fields.state,
    zip: fields.zip,
    lat: coordinates.lat,
    lng: coordinates.lng,
    delivery_only: false,
    hidden: false,
  };
};

export const normalizePrismicHempRetailers = (data) => {
  const retailers = Array.isArray(data && data.retailers)
    ? data.retailers
    : [];

  return retailers.reduce((stores, retailer, index) => {
    const store = parseHempRetailerText(getRetailerText(retailer));

    if (!store) {
      return stores;
    }

    const nameSlug = toSlug(store.name) || "store";
    const zipSlug = toSlug(store.zip) || "zip";

    return [
      ...stores,
      {
        ...store,
        id: `prismic-hemp-retailer-${index}-${nameSlug}-${zipSlug}`,
      },
    ];
  }, []);
};

export const getDistanceMiles = (from, to) => {
  const fromLat = Number(from && from.lat);
  const fromLng = Number(from && from.lng);
  const toLat = Number(to && to.lat);
  const toLng = Number(to && to.lng);

  if (
    !Number.isFinite(fromLat) ||
    !Number.isFinite(fromLng) ||
    !Number.isFinite(toLat) ||
    !Number.isFinite(toLng)
  ) {
    return null;
  }

  const earthRadiusMiles = 3958.8;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRadians(toLat - fromLat);
  const dLng = toRadians(toLng - fromLng);
  const lat1 = toRadians(fromLat);
  const lat2 = toRadians(toLat);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) *
      Math.sin(dLng / 2) *
      Math.cos(lat1) *
      Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusMiles * c;
};

export const getStoreDedupeKey = (store) =>
  [store && store.name, store && store.address, store && store.zip]
    .filter(Boolean)
    .join("|")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const getSortableMiles = (store, searchLocationCoords) => {
  const miles = Number(store && store.miles);

  if (Number.isFinite(miles)) {
    return miles;
  }

  const computedMiles = getDistanceMiles(searchLocationCoords, store);
  return Number.isFinite(computedMiles)
    ? computedMiles
    : Number.MAX_SAFE_INTEGER;
};

export const mergeAndSortStores = (
  apiStores = [],
  customStores = [],
  searchLocationCoords = false,
) => {
  const storesByKey = new Map();

  apiStores.forEach((store) => {
    const key = getStoreDedupeKey(store) || store.id;

    if (!storesByKey.has(key)) {
      storesByKey.set(key, store);
    }
  });

  customStores.forEach((store) => {
    const miles = getDistanceMiles(searchLocationCoords, store);
    const storeWithMiles = Number.isFinite(miles) ? { ...store, miles } : store;
    const key = getStoreDedupeKey(storeWithMiles) || storeWithMiles.id;

    if (!storesByKey.has(key)) {
      storesByKey.set(key, storeWithMiles);
    }
  });

  return Array.from(storesByKey.values()).sort(
    (a, b) =>
      getSortableMiles(a, searchLocationCoords) -
      getSortableMiles(b, searchLocationCoords),
  );
};

export const getStoresPage = (stores = [], page, limit) => {
  const start = (page - 1) * limit;
  return stores.slice(start, start + limit);
};
